'use client';

import Box from '@mui/material/Box';
import { animated, useSprings } from 'react-spring';
import { glöd, tillKombo, type Kombonamn } from './rörelse-konfig';
import type { IFärgKombo } from 'src/theme/NyttFärgtema';
import { grainOverlay } from 'src/theme/styles';

const AnimeradDiv = animated('div');

type Props = {
    kombinationer: (Kombonamn | IFärgKombo)[];
    /** Vilken kombination som lyser nu. */
    index: number;
    /** Var ljuskällan sitter, i procent av ytan. En per kombination. */
    positioner?: [number, number][];
    styrka?: number;
};

/**
 * Ljuskällorna bakom scenerna. Ligger fixerat bakom allt och deltar aldrig i scroll-layout.
 *
 * Lagrad opacity-korsning i stället för att animera själva gradientsträngen: att tona
 * opacity är rent kompositorarbete, medan en interpolerad gradient målar om en yta stor
 * som vyporten varje bildruta.
 */
export function Gradientfält({ kombinationer, index, positioner, styrka = 0.55 }: Props) {
    const [fjädrar] = useSprings(
        kombinationer.length,
        (i) => ({
            opacity: i === index ? 1 : 0,
            config: { tension: 60, friction: 34 }
        }),
        [index]
    );

    return (
        <Box
            aria-hidden
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 0,
                pointerEvents: 'none',
                overflow: 'hidden',
                '&::after': grainOverlay(0.18),
            }}
        >
            {fjädrar.map((stil, i) => {
                const [x, y] = positioner?.[i] ?? [i % 2 === 0 ? 22 : 78, i % 3 === 0 ? 28 : 68];
                return (
                    <AnimeradDiv
                        key={tillKombo(kombinationer[i]).primary.hexkod + i}
                        style={{
                            ...stil,
                            position: 'absolute',
                            left: `${x - 55}%`,
                            top: `${y - 55}%`,
                            width: '110%',
                            height: '110%',
                            background: glöd(kombinationer[i], styrka),
                            filter: 'blur(28px)'
                        }}
                    />
                );
            })}
        </Box>
    );
}
